import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Activity, MapPin, Shield, ArrowRight } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const PetCarePage = () => {
  const navigate = useNavigate();
  const goToReserve = () => navigate('/reserve');

  const scrollToPlans = () => {
    window.location.href = '/#plans';
  };

  const trackingFeatures = [
    {
      icon: Activity,
      title: 'Pet Activity',
      description: 'Daily movement patterns and routine.',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
    },
    {
      icon: MapPin,
      title: 'Pet Location',
      description: 'Location when enabled, right in the Connect app.',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Shield,
      title: 'Safe Zones',
      description: 'Get notified when they leave or return.',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="pet-care-page">
      <Navigation />

      {/* Hero Section with Gradient Background */}
      <section className="section-soft pt-32 pb-16 md:pb-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A1A1A] mb-4">
              Pet Care
            </h1>
            <p className="text-xl sm:text-2xl text-[#5A5A5A] italic mb-4">
              Because they're family too.
            </p>
            <p className="text-base sm:text-lg text-[#4A4A4A] mb-6 max-w-lg mx-auto leading-relaxed">
              Track your pet's activity and location (if enabled) inside the same Connect app.
            </p>

            {/* Price Line */}
            <p className="text-lg font-medium text-[#1A1A1A] mb-6">
              $49 per pet band
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={goToReserve}
                className="bg-[#1A1A1A] text-white px-8 py-3.5 rounded-full text-base font-medium
                           hover:bg-[#333] transition-all duration-300 inline-flex items-center justify-center gap-2"
                data-testid="add-pet-band-btn"
              >
                Add Pet Band
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={scrollToPlans}
                className="bg-white border border-black/10 text-[#1A1A1A] px-8 py-3.5 
                           rounded-full text-base hover:bg-black/5 transition-all duration-300"
                data-testid="see-plans-btn"
              >
                See Plans
              </motion.button>
            </div>

            {/* Reassurance */}
            <p className="text-sm text-[#7A7A7A]">
              Works with any Connect plan.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What You Can Track Section */}
      <section className="section-warm py-16 md:py-24">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-[#1A1A1A] mb-3">
              What you can track
            </h2>
            <p className="text-[#5A5A5A] max-w-md mx-auto">
              Everything you need to keep your pet safe and healthy.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {trackingFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="premium-card text-center"
                data-testid={`tracking-card-${index}`}
              >
                <div className={`w-14 h-14 ${feature.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-5`}>
                  <feature.icon className={`w-7 h-7 ${feature.color}`} />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#5A5A5A] text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Secondary Image Section */}
      <section className="section-soft py-16 md:py-24">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-black/5">
                <img
                  src="https://customer-assets.emergentagent.com/job_screenless-health/artifacts/b14m5fvm_family%204.jpg"
                  alt="Family with pet outdoors"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] mb-4">
                One app for the whole family
              </h2>
              <p className="text-[#5A5A5A] mb-6 leading-relaxed">
                Your pet's data lives alongside your family's health insights. No separate apps, no extra complexity — just one unified experience for everyone you care about.
              </p>
              <ul className="space-y-3">
                {['Real-time activity updates', 'Location history', 'Customizable safe zones'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#4A4A4A]">
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta-section py-16 md:py-24">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-xl mx-auto"
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-[#1A1A1A] mb-4">
              Ready to include your pet?
            </h2>
            <p className="text-[#5A5A5A] mb-8">
              Add a pet band to any Connect plan and keep the whole family connected.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={goToReserve}
              className="bg-[#1A1A1A] text-white px-10 py-4 rounded-full text-base font-medium
                         hover:bg-[#333] transition-all duration-300 inline-flex items-center gap-2"
              data-testid="final-cta-btn"
            >
              Add Pet Band
              <ArrowRight className="w-4 h-4" />
            </motion.button>
            <p className="text-sm text-[#7A7A7A] mt-4">
              $49 per pet band • Works with any plan
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PetCarePage;
